const GROUPED_FIELDS = {
    width: 'width',
    profile: 'profile',
    rim: 'rim',
    diameter: 'rim',
};

const ALL_LABELS = {
    width: 'All Sizes',
    profile: 'All Profiles',
    rim: 'All Rims',
    diameter: 'All Rims',
};

function asText(value) {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value);
}

function selectedRimValue(selected) {
    if (!selected || typeof selected !== 'object') {
        return '';
    }

    if (Object.prototype.hasOwnProperty.call(selected, 'diameter')) {
        return asText(selected.diameter);
    }

    return asText(selected.rim);
}

function normalizeSelection(selected) {
    if (!selected || typeof selected !== 'object') {
        return {
            width: '',
            profile: '',
            rim: '',
        };
    }

    return {
        width: asText(selected.width),
        profile: asText(selected.profile),
        rim: selectedRimValue(selected),
    };
}

function combinationMatches(size, selected, exceptField) {
    if (!size || typeof size !== 'object') {
        return false;
    }

    if (exceptField !== 'width' && selected.width && asText(size.width) !== selected.width) {
        return false;
    }

    if (exceptField !== 'profile' && selected.profile && asText(size.profile) !== selected.profile) {
        return false;
    }

    if (exceptField !== 'rim' && selected.rim && asText(size.rim) !== selected.rim) {
        return false;
    }

    return true;
}

export default {
    methods: {
        mostPopularCombinations() {
            if (Array.isArray(this.mostPopular)) {
                return this.mostPopular;
            }

            const source = this.options ?? this.sizeOptions ?? {};

            return Array.isArray(source.most_popular) ? source.most_popular : [];
        },
        sizeCombinations() {
            if (Array.isArray(this.combinations)) {
                return this.combinations;
            }

            const source = this.options ?? this.sizeOptions ?? {};

            return Array.isArray(source.combinations) ? source.combinations : [];
        },
        /**
         * Values still valid for this field given the other selected size parts.
         * Returns null when combination data is unavailable so callers keep the full list.
         */
        refinedValues(field, selectedSizes) {
            const comboField = GROUPED_FIELDS[field] ?? field;
            const combinations = this.sizeCombinations();

            if (!combinations.length || !['width', 'profile', 'rim'].includes(comboField)) {
                return null;
            }

            const selected = normalizeSelection(selectedSizes);
            const values = new Set();

            combinations.forEach((size) => {
                if (!combinationMatches(size, selected, comboField)) {
                    return;
                }

                const value = asText(size[comboField]);

                if (value) {
                    values.add(value);
                }
            });

            return values;
        },
        buildSizeGroups(option, selectedSizes) {
            const field = GROUPED_FIELDS[option?.name];

            if (!field) {
                return null;
            }

            let available = Array.isArray(option?.data) ? option.data : [];
            const selected = normalizeSelection(selectedSizes);
            const refined = this.refinedValues(option.name, selected);

            if (refined) {
                available = available.filter((item) => refined.has(asText(item)));
            }

            const availableKeys = new Set(available.map(asText));
            const popularKeys = new Set();

            this.mostPopularCombinations().forEach((size) => {
                if (!combinationMatches(size, selected, field)) {
                    return;
                }

                const value = asText(size[field]);

                if (availableKeys.has(value)) {
                    popularKeys.add(value);
                }
            });

            return {
                popular: available.filter((item) => popularKeys.has(asText(item))),
                rest: available.filter((item) => !popularKeys.has(asText(item))),
                allLabel: ALL_LABELS[option.name] ?? 'All Sizes',
            };
        },
        rememberPopularSize(name, event) {
            const value = event?.target?.value ?? '';

            if (name === 'width') {
                this.selectedWidth = value;
            }

            if (name === 'profile') {
                this.selectedProfile = value;
            }

            if (name === 'rim' || name === 'diameter') {
                this.selectedRim = value;
            }
        },
        /**
         * Keep local selection state and form selects in sync after a size change.
         * Pass selectedTarget when the form binds width/profile/rim onto a shared object.
         */
        onSizeFieldChange(name, event, selectedTarget = null) {
            this.rememberPopularSize(name, event);

            const selected = selectedTarget && typeof selectedTarget === 'object'
                ? selectedTarget
                : {
                    width: this.selectedWidth,
                    profile: this.selectedProfile,
                    rim: this.selectedRim,
                };

            if (name === 'width') {
                selected.width = event?.target?.value ?? '';
            }

            if (name === 'profile') {
                selected.profile = event?.target?.value ?? '';
            }

            if (name === 'rim' || name === 'diameter') {
                const rimKey = Object.prototype.hasOwnProperty.call(selected, 'diameter') ? 'diameter' : 'rim';
                selected[rimKey] = event?.target?.value ?? '';
            }

            this.clearInvalidSizeSelections(name, selected);

            this.selectedWidth = selected.width ?? '';
            this.selectedProfile = selected.profile ?? '';
            this.selectedRim = selectedRimValue(selected);

            this.resetClearedSizeSelects(selected, name);
        },
        resetClearedSizeSelects(selected, changedField) {
            const root = this.$el;

            if (!root || typeof root.querySelector !== 'function') {
                return;
            }

            const rimKey = Object.prototype.hasOwnProperty.call(selected, 'diameter') ? 'diameter' : 'rim';
            const fields = {
                width: selected.width,
                profile: selected.profile,
                rim: selected[rimKey],
                diameter: selected[rimKey],
            };

            Object.keys(fields).forEach((field) => {
                if (field === changedField || fields[field]) {
                    return;
                }

                const el = root.querySelector(`#${field}, select[name="${field}"]`);

                if (el && el.value) {
                    el.value = '';
                }
            });
        },
        clearInvalidSizeSelections(changedField, selected) {
            if (!selected || typeof selected !== 'object') {
                return;
            }

            const rimKey = Object.prototype.hasOwnProperty.call(selected, 'diameter') ? 'diameter' : 'rim';
            const next = {
                width: asText(selected.width),
                profile: asText(selected.profile),
                rim: asText(selected[rimKey]),
            };

            const changed = GROUPED_FIELDS[changedField] ?? changedField;

            // Re-check each other field against the latest selection.
            ['width', 'profile', 'rim'].forEach((field) => {
                if (field === changed) {
                    return;
                }

                const current = next[field];

                if (!current) {
                    return;
                }

                const valid = this.refinedValues(field, next);

                if (valid && !valid.has(current)) {
                    if (field === 'width') {
                        selected.width = '';
                        next.width = '';
                    }

                    if (field === 'profile') {
                        selected.profile = '';
                        next.profile = '';
                    }

                    if (field === 'rim') {
                        selected[rimKey] = '';
                        next.rim = '';
                    }

                    if (Object.prototype.hasOwnProperty.call(selected, 'speed')) {
                        selected.speed = '';
                    }
                }
            });
        },
    },
};
